import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import type { SxProps, Theme, TypographyVariant } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import parse from 'html-react-parser';
import { t } from 'i18next';
import { ReactNode, useMemo } from 'react';
import { Link as RouterLink } from 'react-router';

import { BasicChip } from '@/components/Chips/BasicChip';
import type { Hcphco } from '@/core/graphql/generated/types';
import { returnValidIntl } from '@/utils/isValidIntl';
import { mergeSx } from '@/utils/mergeSx';
import { useBreakpoint } from '@/utils/useBreakpoint';

import { useProfileMetaData } from '../HcpCard/_hooks/useProfileMetaData';

import { profileNameStyles as sx } from './ProfileName.styles';

type Props = {
	profile: Hcphco | null;
	withDegrees?: boolean;
	withInternationalName?: boolean;
	withNickname?: boolean;
	withShortName?: boolean;
	withProfileLink?: boolean;
	withInactive?: boolean;
	withLink?: boolean;
	chips?: ReactNode;

	inline?: boolean;
	shouldValidateMarkup?: boolean;

	noWrap?: boolean;
	wrapChips?: boolean;
	lineClamp?: number;
	variant?: TypographyVariant;
	customSx?: {
		root?: SxProps<Theme>;
		name?: SxProps<Theme>;
	};
};

export const ProfileName = ({
	profile,
	withDegrees,
	withInternationalName,
	withNickname,
	withShortName,
	withProfileLink,
	withInactive = false,
	withLink,
	chips,

	shouldValidateMarkup = true,
	noWrap = false,
	wrapChips = true,

	inline = false,
	lineClamp,
	variant = 'body1',
	customSx
}: Props) => {
	const { getProfileName, profileLink, internationalName, englishName } =
		useProfileMetaData(profile);
	const isSmall = useBreakpoint('sm', 'down');

	const { name, rawName, degrees } = useMemo(() => {
		const { name: profileName, degrees: profileDegrees } = getProfileName({
			withInternationalName: withInternationalName && inline,
			withShortName,
			withNickname
		});

		return {
			name: (shouldValidateMarkup ? parse(profileName) : profileName) as string,
			rawName: profileName,
			degrees: profileDegrees
		};
	}, [
		getProfileName,
		withInternationalName,
		inline,
		withShortName,
		withNickname,
		shouldValidateMarkup
	]);

	const internationalNames = useMemo(
		() =>
			[
				returnValidIntl(rawName, internationalName),
				returnValidIntl(rawName, englishName)
			].filter(Boolean),
		[rawName, internationalName, englishName]
	);

	const content = (
		<Typography
			variant={variant}
			noWrap={noWrap}
			sx={mergeSx(
				customSx?.name,
				!!lineClamp && {
					display: '-webkit-box',
					'-webkitLineClamp': lineClamp.toString(),
					'-webkitBoxOrient': 'vertical',
					overflow: 'hidden',
					textOverflow: 'ellipsis'
				}
			)}
		>
			<Box
				component='span'
				sx={{
					mr: withDegrees || inline ? 0 : 1
				}}
			>
				{name}
			</Box>

			<Box component='span' sx={sx.degrees(inline && !wrapChips, !!withLink)}>
				{degrees}
			</Box>

			{wrapChips &&
				(chips || (withInactive && profile?.statusCda_VId === 'iact')) && (
					<Box component='span' sx={sx.chips}>
						{chips}

						{withInactive && profile?.statusCda_VId === 'iact' && (
							<BasicChip label={t('Inactive')} />
						)}
					</Box>
				)}
		</Typography>
	);

	return (
		<Box sx={mergeSx(sx.root, customSx?.root)}>
			<Box sx={sx.name}>
				{withLink ? (
					<Link component={RouterLink} to={profileLink} underline='none'>
						{content}
					</Link>
				) : (
					content
				)}

				{!wrapChips &&
					(chips || (withInactive && profile?.statusCda_VId === 'iact')) && (
						<Box component='span' sx={sx.chips}>
							{chips}

							{withInactive && profile?.statusCda_VId === 'iact' && (
								<BasicChip label={t('Inactive')} />
							)}
						</Box>
					)}

				{withProfileLink &&
					profileLink &&
					(isSmall ? (
						<Tooltip title={t('Open Profile')}>
							<IconButton
								component={RouterLink}
								to={profileLink}
								target='_blank'
								rel='noopener'
								color='primary'
								sx={sx.profileLink}
							>
								<OpenInNewIcon />
							</IconButton>
						</Tooltip>
					) : (
						<Button
							variant='text'
							startIcon={<OpenInNewIcon />}
							component={RouterLink}
							to={profileLink}
							target='_blank'
							rel='noopener'
							sx={sx.profileLink}
						>
							{t('Open Profile')}
						</Button>
					))}
			</Box>

			{!inline && withInternationalName && internationalNames.length > 0 && (
				<Typography variant={variant} noWrap={noWrap}>
					{internationalNames.join(' | ')}
				</Typography>
			)}
		</Box>
	);
};
